import React, { useId } from 'react';

import {
  Box,
  InputLabelProps,
  SelectProps,
  listClasses,
} from '@mui/material';

import { FieldLabel } from '~/components/FieldLabel';
import { DropdownIcon } from '~/components/icons/DropdownIcon';
import { LoaderOverlay } from '~/components/Loader';

import { CustomDarkInput, CustomLightInput, Select } from './styled';

/**
 * @function Finds children that have attribute "value"
 * @param children - React.ReactNode
 * @see {@link https://github.com/mui/material-ui/blob/master/packages/mui-material/src/Select/SelectInput.js}
 * @returns children that can be used as options
 */
export const toOptions = (children?: React.ReactNode): React.ReactElement[] => {
  return React.Children.toArray(children)
    .filter(child => React.isValidElement(child) && {}.hasOwnProperty.call(child.props, 'value')) as React.ReactElement[];
}

export interface IDropdown extends Omit<SelectProps, 'ref'> {
  dark?: boolean;
  label?: React.ReactNode;
  InputLabelProps?: Partial<InputLabelProps>;
  valueLabel?: React.ReactNode;
  isLoading?: boolean;
  loadingLabel?: React.ReactNode;
  customRenderValue?: boolean;
  children?: React.ReactNode;
}

export const Dropdown: React.FC<IDropdown> = (props) => {
  const {
    id: selectId,
    dark,
    isLoading,
    label,
    InputLabelProps,
    valueLabel,
    displayEmpty,
    multiple,
    loadingLabel = 'Loading ...',
    children,
    customRenderValue,
    renderValue,
    ...others
  } = props;
  const internalId = useId();
  const id = selectId ? selectId : label ? internalId : undefined;
  
  return (
    <>
      {label && <FieldLabel htmlFor={id}  {...InputLabelProps}>{label}</FieldLabel>}
      <Select
        id={id}
        dark={dark}
        input={dark ? <CustomDarkInput /> : <CustomLightInput />}
        IconComponent={DropdownIcon}
        displayEmpty={displayEmpty}
        multiple={multiple}
        renderValue={(value: string) => {
          const shouldRenderValue = renderValue && (customRenderValue || multiple || (displayEmpty && !value));
          const optionLabel: React.ReactNode = shouldRenderValue
            ? renderValue(value)
            : toOptions(children).find((child) => child.props.value === value)?.props?.children;

          return (
            <>
              {valueLabel ? (
                <Box
                  htmlFor={id}
                  component="label"
                  sx={{
                    cursor: 'pointer',
                    padding: '0 20px 0 0',
                    color: ({ palette }) => palette.primary.light,
                  }}
                >
                  {valueLabel}
                </Box>
              ) : null}
              {isLoading && !optionLabel && loadingLabel}
              {optionLabel}
            </>
          );
        }}
        MenuProps={{
          sx: {
            [`&& .${listClasses.root}`]: {
              padding: 0,
              position: 'relative',
              maxHeight: '50vh',
              minHeight: isLoading ? 118 : undefined,
            },
          },
        }}
        {...others}
      >
        {isLoading && (
          <LoaderOverlay
            sx={{
              bgcolor: ({ palette }) => palette.background.paper,
              '> div': { top: '8px', left: 0 },
            }} />
        )}
        {children}
      </Select>
    </>
  );
};
